import { faArrowUpRightFromSquare, faXmark } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
    Bar,
    BarChart,
    Cell,
    LabelList,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import ChartCard from './ChartCard.tsx';
import ChartTooltip from './ChartTooltip.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import Table, { RowControlIconButton } from '../../core/components/Table.tsx';
import classnames from '../../core/utils/classnames.tsx';

const CloseIcon = withIcon(faXmark);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);

interface ChartProps {
    data: any[];
    selectedKey?: string | null;
    onSelectedKeyChange?: (key: string | null) => void;
}

const AssistanceReasonsChart = ({ data, selectedKey, onSelectedKeyChange }: ChartProps) => {
    const { t, i18n } = useTranslation('analytics');

    const getFieldLabel = (fieldName: string) => {
        // Probably field names should have their translation strings in a more general name space
        if (i18n.exists(`assistance:itemsView.fieldNames.${fieldName}`)) {
            return t(`assistance:itemsView.fieldNames.${fieldName}`);
        } else if (i18n.exists(`assistance:headerView.fieldNames.${fieldName}`)) {
            return t(`assistance:headerView.fieldNames.${fieldName}`);
        } else if (i18n.exists(`config:configPage.document.validationCheckNames.${fieldName}`)) {
            return t(`config:configPage.document.validationCheckNames.${fieldName}`);
        } else {
            return fieldName;
        }
    };
    const assistanceReasons = data?.map((item) => ({
        label: t(`dashboard.charts.assistanceReasons.${item.reason}`, { fieldName: getFieldLabel(item.fieldName) }),
        value: Math.round(item.percentage * 100),
        key: `${item.reason}:${item.fieldName}`,
    }));

    const gap = 10;
    const barSize = 40;
    const height = data.length * (barSize + gap) + gap;

    const maxLabelLength = assistanceReasons.reduce((acc, item) => {
        return Math.max(acc, item.label.length);
    }, 0);
    const yAxisWidth = maxLabelLength * 7 + 20;

    const unit = '%';
    const labelFormatter = (value: number) => `${value}${unit}`;

    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={assistanceReasons} layout="vertical" margin={{ right: 30 }}>
                <RechartsTooltip
                    cursor={false}
                    content={({ active, payload }) => {
                        return <ChartTooltip content={labelFormatter(payload?.[0]?.payload?.value)} />;
                    }}
                />
                <XAxis type="number" hide width={100} />
                <YAxis
                    dataKey="label"
                    type="category"
                    axisLine={false}
                    tickLine={false}
                    tickMargin={10}
                    width={yAxisWidth}
                    tick={{ fill: 'inherit' }}
                    className="text-xs fill-content-strong"
                />
                <Bar
                    dataKey="value"
                    className="fill-data-data1 transition-colors"
                    layout="vertical"
                    barSize={barSize}
                    onClick={({ payload }, index) => {
                        onSelectedKeyChange(payload.key);
                    }}
                >
                    <LabelList dataKey="value" position="right" formatter={labelFormatter} className="text-xs" />
                    {assistanceReasons.map(({ key }, index) => (
                        <Cell
                            key={`cell-${index}`}
                            className={classnames(
                                'transition-colors cursor-pointer hover:fill-data-data1',
                                selectedKey && key != selectedKey ? 'fill-data-data1/50' : 'fill-data-data1'
                            )}
                        />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

const AssistanceReasonsCard = ({
    data,
    ...props
}: Omit<React.ComponentPropsWithRef<typeof ChartCard>, 'title' | 'helpText' | 'controls'> & { data: any[] }) => {
    const { t } = useTranslation('analytics');

    const isDataValid = data != null && Array.isArray(data) && data.length > 0;

    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [selectedReason, selectedFieldName] = selectedKey ? selectedKey.split(':') : [null, null];
    const selectedData = data.find((item) => {
        return item.reason === selectedReason && item.fieldName === selectedFieldName;
    });

    return (
        <ChartCard
            title={t('dashboard.charts.assistanceReasons.title')}
            helpText={t('dashboard.charts.assistanceReasons.helpText')}
            {...props}
        >
            {isDataValid && (
                <div className="flex gap-10 w-full xl:flex-row flex-col items-start justify-start h-full self-stretch">
                    <div className="flex-2 w-full">
                        <AssistanceReasonsChart
                            data={data}
                            selectedKey={selectedKey}
                            onSelectedKeyChange={setSelectedKey}
                        />
                    </div>
                    {selectedKey ? (
                        <Table className="min-w-0 xl:max-w-140 xl:w-1/2 w-full max-w-full">
                            <Table.Head>
                                <Table.Row>
                                    <Table.HeadCell>{t('dashboard.charts.assistanceReasons.sampleUrl')}</Table.HeadCell>
                                    <Table.HeadCell className="w-16">
                                        <RowControlIconButton onClick={() => setSelectedKey(null)} className="-my-1">
                                            <CloseIcon />
                                        </RowControlIconButton>
                                    </Table.HeadCell>
                                </Table.Row>
                            </Table.Head>
                            <Table.Body>
                                {selectedData?.sampleUrls?.map((sampleUrl, index) => (
                                    <Table.Row key={index}>
                                        <Table.Cell>
                                            <Link
                                                to={sampleUrl}
                                                target="_blank"
                                                className="hover:underline underline-offset-2 w-full flex"
                                            >
                                                <span className="truncate min-w-0 max-w-full">{sampleUrl}</span>
                                            </Link>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <RowControlIconButton asChild>
                                                <Link to={sampleUrl} target="_blank">
                                                    <ExternalLinkIcon />
                                                </Link>
                                            </RowControlIconButton>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    ) : (
                        <div className="min-w-0 xl:max-w-140 xl:w-1/2 w-full max-w-full rounded bg-surface-medium border border-line-weak flex items-center justify-center p-6 text-sm text-content-medium text-center self-stretch">
                            {t('dashboard.charts.assistanceReasons.noSelectedReason')}
                        </div>
                    )}
                </div>
            )}
        </ChartCard>
    );
};

export default AssistanceReasonsCard;
